import { Directive, Input, TemplateRef, ViewContainerRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Util } from 'app/models/util';
import { LpmConfig } from 'app/models/lpm.config';

@Directive({
  selector: '[appIsDisplayed]',
  standalone: true
})
export class IsDisplayedDirective implements OnInit, OnChanges {
  @Input() appIsDisplayed: keyof LpmConfig | (keyof LpmConfig)[];
  @Input() appIsDisplayedElse: TemplateRef<any> | null = null;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private util: Util
  ) {}

  ngOnInit() {
    this.updateView();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['appIsDisplayed'] || changes['appIsDisplayedElse']) {
      this.updateView();
    }
  }

  private updateView() {
    this.viewContainer.clear();

    if (this.checkPermission()) {
      this.viewContainer.createEmbeddedView(this.templateRef);
    } else if (this.appIsDisplayedElse) {
      this.viewContainer.createEmbeddedView(this.appIsDisplayedElse);
    }
  }

  private checkPermission(): boolean {
    if (!this.appIsDisplayed) {
      return true; // IF no binding provided, we default to showing it
    }

    const params = this.util.getWebComponentInputParams();
    const lpmConfig = params?.lpmConfig;

    if (!lpmConfig) {
      return true; 
    }

    // Convert into an array to support one or more conditions
    const conditions = Array.isArray(this.appIsDisplayed) ? this.appIsDisplayed : [this.appIsDisplayed];

    // Explicitly check that ALL conditions are met
    return conditions.every(condition => {
      switch (condition) {
        case 'allowRename':
          return lpmConfig.allowRename === true;
        case 'allowCopy':
          return lpmConfig.allowCopy === true;
        case 'allowMove':
          return lpmConfig.allowMove === true;
        case 'allowPreview':
          return lpmConfig.allowPreview === true;
        case 'allowEdit':
          return lpmConfig.allowEdit === true;
        case 'allowShare':
          return lpmConfig.allowShare === true;
        case 'isInternalShareSupported':
          return lpmConfig.isInternalShareSupported === true;
        case 'isExternalShareSupported':
          return lpmConfig.isExternalShareSupported === true;
        case 'viewPermissions':
          return lpmConfig.viewPermissions === true;
        case 'allowTrash':
          return lpmConfig.allowTrash === true;
        case 'emptyTrashButtonWithLabel':
          return lpmConfig.emptyTrashButtonWithLabel === true;
        case 'allowStar':
          return lpmConfig.allowStar === true;
        case 'allowLabel':
          return lpmConfig.allowLabel === true;
        case 'allowAddNotes':
          return lpmConfig.allowAddNotes === true;
        case 'viewNotes':
          return lpmConfig.viewNotes === true;
        case 'isUserTaggingSupported':
          return lpmConfig.isUserTaggingSupported === true;
        case 'allowDownload':
          return lpmConfig.allowDownload === true;
        case 'downloadConfirmButtonIcon':
          return lpmConfig.downloadConfirmButtonIcon === true;
        case 'fullExplorer':
          return lpmConfig.fullExplorer === true;
        case 'isSupportUser':
          return lpmConfig.isSupportUser === true;
        case 'globalUploadPopup':
          return lpmConfig.globalUploadPopup === true;
        case 'showTrashPageTitle':
          return lpmConfig.showTrashPageTitle === true;
        default:
          // In case 'host' or 'scope' is passed or unhandled config
          return lpmConfig[condition] ? true : false;
      }
    });
  }
}
